import React, {Component} from 'react';
import css from './Users.module.scss';
import Accordion from "../../../components/Accordion/Accordion";
import axios from "axios";
import {Formik, Form, Field, ErrorMessage} from 'formik';
import ErrorLabel from "../../../components/ErrorLabel/ErrorLabel";
import {connect} from "react-redux";
import {EMAIL_REGEX} from "../../../constants";
import Alert from "../../../components/Alert/Alert";


/**
 * Users section in Settings page.
 */
const Users = class extends Component {
    // ---------- class fields: ----------
    source = axios.CancelToken.source();

    roleOptions = [
        {label: 'Sales',        value: 'rep'},
        {label: 'Field Dept',   value: 'field'},
        {label: 'Partners', value: 'partner'},
    ];

    subroleOptions= [
        {label: 'Sales Manager',  value: 'lead'},
        {label: 'Sales Rep',      value: 'rep'},
        {label: 'Surveyor',       value: 'survey'},
        {label: 'Installer',      value: 'install'},
        {label: 'Inspector',      value: 'inspect'},
        {label: 'Electrician',    value: 'electric'},
        {label: 'Contractor',     value: 'contract'},
    ];

    // ---------- state: ----------
    state = {
        shouldShowAlert: false,
        alertMessage: '',
        selectedOffice: '',
        officeOptions: [],
        filterKeyword: '',
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        const officeOptions = Object.keys(this.props.userData)
            .map(office => {
                return {label: office, value: office};
            });

        officeOptions.sort(function(a, b) {
            var textA = a.label.toUpperCase();
            var textB = b.label.toUpperCase();
            return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        });

        const selectedOffice = officeOptions.length ? officeOptions[0].value : '';

        this.setState({
            officeOptions,
            selectedOffice,
        });
    }

    componentWillUnmount() {
        this.source.cancel();
    }

    // ---------- methods: ----------
    /**
     * Handle filter input change when user enters keyword(s).
     *
     * Filters the displayed user data by user keyword. N.b. filters by user name as well as SalesForce Record.
     *
     * @returns {undefined}: Sets state.
     */
    handleFilterOnChange = (val) => {
        this.setState({
            filterKeyword: val.toLowerCase()
        });
    };

    displayedData = () => {
        let displayedData = [];

        // n.b. we need this conditional guard to prevent trying to index undefined:
        if (this.state.selectedOffice) {
            displayedData = this.props.userData[this.state.selectedOffice]
                // Map only the properties we need:
                .map(user => {
                    return {
                        id: user.Id, // string
                        name: user.Name, // string
                        email: user.Email, // string
                        commissionRate: user.Commission_Rate__c, // number
                        role: user.role, // string
                        subrole: user.subrole, // string
                        jobDepartment: user.Job_Department__c, // string
                        jobTitle: user.Job_Title__c, // string
                        localId: user.localId, // string | false
                        active: user.Active__c ,// boolean
                        shouldShowForm: user.Active__c && user.Dashboard_Login__c && (user.Termination_Date__c === null) // boolean
                    };
                })
                // filter array by whether the filter keyword matches the `name` property in result item:
                // n.b. the filtering is case-insensitive
                .filter(user => {
                    const name = user.name.toLowerCase();
                    const salesForceId = user.id.toLowerCase();
                    return name.includes(this.state.filterKeyword) || salesForceId.includes(this.state.filterKeyword);
                });
        }

        displayedData.sort(function(a, b) {
            var textA = a.name.toUpperCase();
            var textB = b.name.toUpperCase();
            return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        });

        return displayedData;
    };

    /**
     * @param val {string}
     **/
    selectOfficeOnChange = (val) => {
        this.setState({
            selectedOffice: val
        });
    };

    deactivateUserOnClick = (name, email, role) => {
        const payload = {
            name,
            email,
            role,
            apiId: this.props.user.apiId
        };

        // n.b. if user logged-in user's role is 'office', then we are not allowed to deactivate (this is controlled in the back-end):
        // TODO: need some sort of notification for user to know if success/failed response: a page-level flash message
        // TODO: need to update back-end to support response message -- see `role && role != 'office'` in `UserController.js`
        axios.put(
            `/user/deactivate/${this.props.user.id}`,
            payload,
            {
                cancelToken: this.source.token
            })
            .catch(err => console.log(err));
    };

    render() {
        return (
            <div className={css.Users}>
                <Alert showAlert={this.state.shouldShowAlert}>{this.state.alertMessage}</Alert>

                <div className="row">
                    <div className="col-sm-6 col-lg-4">
                        <input
                            type="text"
                            className="input input-plain"
                            placeholder="Filter Results"
                            onChange={(event) => this.handleFilterOnChange(event.target.value)} />
                    </div>
                    <div className="col-sm-6 col-lg-4 offset-lg-4 mt-3 mt-sm-0">
                        <select onChange={(event) => this.selectOfficeOnChange(event.target.value)}>
                            {
                                // TODO: set default selected value:
                                this.state.officeOptions.map(option => <option key={option.label} value={option.value}>{option.value}</option>)
                            }
                        </select>
                    </div>
                </div>

                {/* ---------- user list: ----------  */}
                <ul className={css.Users__List}>
                    <li className="row mt-5 font-weight-bold">
                        <div className="col-6">Sales Rep</div>
                        <div className="col-4 text-center">SF Record #</div>
                        <div className="col-2 text-center">$ / kW</div>
                    </li>
                    {
                        this.displayedData().map(user => {
                            return (
                                <li key={user.id}>
                                    <Accordion
                                        user={user}
                                        header={
                                            <>
                                                <div className="row">
                                                    <div className="col-sm-6">
                                                        <div className="text-primary font-weight-bold">{user.name}</div>
                                                        {/* if user doesn't have `localId` then we can't make API calls; similarly, we don't want to display user if not active: */}
                                                        <div className={user.active && !user.localId ? 'text-danger text-sm' : 'd-none'}>No Login</div>
                                                        <div className={!user.active ? 'text-danger text-sm' : 'd-none'}>Inactive</div>
                                                    </div>
                                                    <div className="col-sm-4 mt-3 mt-sm-0 text-center">
                                                        {user.id}
                                                    </div>
                                                    <div className="col-sm-2 mt-3 mt-sm-0 text-center">
                                                        {user.commissionRate}
                                                    </div>
                                                </div>
                                            </>
                                        }
                                        content={
                                            <>
                                                {/* ---------- role section: ----------  */}
                                                <div className={user.localId && user.shouldShowForm ? '' : 'd-none'}>
                                                    <Formik
                                                        initialValues={{
                                                            role: user.role,
                                                            subrole: user.subrole,
                                                        }}

                                                        onSubmit={
                                                            (values) => {
                                                                const subStatus = values.subrole ? true : false;
                                                                const roleStatus = values.role ? true : false;

                                                                const payload = {
                                                                    role: values.role,
                                                                    subrole: values.subrole,
                                                                    roleStat: roleStatus,
                                                                    subStat: subStatus
                                                                };

                                                                axios.put(
                                                                    `/user/role/${user.localId}`,
                                                                    payload,
                                                                    {
                                                                        cancelToken: this.source.token
                                                                    })
                                                                    .catch(err => console.log('Error updating role:\n', err));
                                                            }
                                                        }
                                                    >
                                                        <Form>
                                                            <div>
                                                                <h4 className="text-center">Role</h4>

                                                                <div className="row mt-3">
                                                                    <div className="col-sm-3 text-right">Role</div>
                                                                    <div className="col-sm-6">
                                                                        <Field name="role" as="select">
                                                                            <option value=""></option>
                                                                            { this.roleOptions.map(role => {
                                                                                return (<option key={role.label} defaultValue={role.value === user.role} value={role.value}>{role.label}</option>);
                                                                            }) }
                                                                        </Field>
                                                                    </div>
                                                                </div>

                                                                <div className="row mt-3">
                                                                    <div className="col-sm-3 text-right">Subrole</div>
                                                                    <div className="col-sm-6">
                                                                        <Field name="subrole" as="select">
                                                                            <option value=""></option>
                                                                            { this.subroleOptions.map(role => {
                                                                                return (<option key={role.label} defaultValue={role.value === user.role} value={role.value}>{role.label}</option>);
                                                                            }) }
                                                                        </Field>
                                                                    </div>
                                                                </div>

                                                                <div className="row mt-3">
                                                                    <div className="col-sm-6 offset-sm-3 text-right">
                                                                        <button className="btn btn-primary" type="submit">Update Roles</button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Form>
                                                    </Formik>
                                                </div>

                                                {/* ---------- update login: ---------- */}
                                                <div className={user.shouldShowForm ? '' : 'd-none'}>
                                                    <Formik
                                                        initialValues={{
                                                            email: user.email,
                                                            password: '',
                                                        }}

                                                        validate={
                                                            (values)=> {
                                                                const errors = {};

                                                                if (!values.email.trim()) {
                                                                    errors.email = 'Please Enter An Email Address';
                                                                } else if (!EMAIL_REGEX.test(values.email)) {
                                                                    errors.email = 'Invalid Email Address';
                                                                }

                                                                if (!values.password.trim()) {
                                                                    errors.password = 'Please Enter Your Password';
                                                                } else if (values.password.length < 8) {
                                                                    errors.password = 'Password must be at least 8 characters';
                                                                }

                                                                return errors;
                                                            }
                                                        }

                                                        onSubmit={
                                                            (values) => {
                                                                const payload ={
                                                                    name:       user.name,
                                                                    email:      values.email,
                                                                    password:   values.password.trim(),
                                                                };

                                                                // if `loginId` exists, then we only need to update user:
                                                                if (user.localId) {
                                                                    axios.put(
                                                                        `/user/${user.localId}`,
                                                                        payload,
                                                                        {
                                                                            cancelToken: this.source.token
                                                                        })
                                                                        .then((res) => {
                                                                            // after successfully updating user, show banner:
                                                                            this.setState({
                                                                                alertMessage: 'User/Login updated successfully.',
                                                                                shouldShowAlert: true,
                                                                            });

                                                                            setTimeout(() => {
                                                                                this.setState({
                                                                                    shouldShowAlert: false,
                                                                                });
                                                                            }, 5500);
                                                                        })
                                                                        .catch(err => console.log(`(PUT) Error in User Page: ${err}`));
                                                                }
                                                                // if `loginId` doesn't exist, we need to create a new one:
                                                                else {
                                                                    // n.b. we are setting the apiId for the NEW user (therefore don't confuse it with `this.props.user.apiId`,
                                                                    // which is the apiId of the currently logged in user):
                                                                    payload.apiId       = user.id;
                                                                    payload.username    = values.email;
                                                                    payload.triggers    = {};

                                                                    // set the role and subrole based on the user's job department:
                                                                    switch (user.jobDepartment) {
                                                                        case 'Survey':
                                                                            payload.role = 'field';
                                                                            payload.subrole = 'survey';
                                                                            break;
                                                                        case 'Installation':
                                                                            payload.role = 'field';
                                                                            payload.subrole = 'install';
                                                                            break;
                                                                        case 'Inspection':
                                                                            payload.role = 'field';
                                                                            payload.subrole = 'inspect';
                                                                            break;
                                                                        case 'Electrician':
                                                                            payload.role = 'field';
                                                                            payload.subrole = 'electric';
                                                                            break;
                                                                        case 'Contractor':
                                                                            payload.role = 'field';
                                                                            payload.subrole = 'contract';
                                                                            break;
                                                                        case 'Partners':
                                                                            payload.role = 'partner';
                                                                            payload.subrole= 'rep';
                                                                            break;
                                                                        default:
                                                                            payload.role = 'rep';
                                                                            payload.subrole = 'rep';
                                                                    }
                                                                    if (user.jobTitle === 'Sales Manager') {
                                                                        payload.subrole = 'lead';
                                                                    }

                                                                    // finally, post data:
                                                                    axios.post(
                                                                        '/user',
                                                                        payload,
                                                                        {
                                                                            cancelToken: this.source.token
                                                                        })
                                                                        .then((res) => {
                                                                            // after successfully updating user, show banner and then refresh page (to force data re-fetch):
                                                                            this.setState({
                                                                                alertMessage: 'Login created successfully.',
                                                                                shouldShowAlert: true,
                                                                            });

                                                                            setTimeout(() => {
                                                                                this.setState({
                                                                                    shouldShowAlert: false,
                                                                                });

                                                                                window.location.reload();
                                                                            }, 3000);
                                                                        })
                                                                        .catch(err => {
                                                                            this.setState({
                                                                                alertMessage: 'There was an issue creating the user account.',
                                                                                shouldShowAlert: true,
                                                                            });

                                                                            setTimeout(() => {
                                                                                this.setState({
                                                                                    shouldShowAlert: false,
                                                                                });
                                                                            }, 5500);
                                                                            console.log(`(POST) Error in User Page: ${err}`)
                                                                        });
                                                                }
                                                            }
                                                        }
                                                    >
                                                        <Form>
                                                            <div className="row mt-4">
                                                                <div className="col-sm-6 offset-sm-3">
                                                                    <Field className="input input-plain" name="email" type="text" />
                                                                    <Field className="input input-plain mt-3" name="password" type="password" placeholder="Password" />
                                                                </div>
                                                            </div>
                                                            <div className="row mt-3">
                                                                <div className="col-sm-6 offset-sm-3 text-right">
                                                                    <button className="btn btn-primary" type="submit">
                                                                        {user.localId ? 'Update Local Login' : 'Create Local Login'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="row mt-3">
                                                                <div className="col-sm-6 offset-sm-3">
                                                                    <ErrorMessage name="email" component={ErrorLabel} />
                                                                    <ErrorMessage name="password" component={ErrorLabel} />
                                                                </div>
                                                            </div>
                                                        </Form>
                                                    </Formik>
                                                </div>

                                                {/* ---------- deactivate user: ---------- */}
                                                <div className={user.localId ? 'text-right mt-3' : 'd-none'}>
                                                    <button className="btn btn-light" type="button" onClick={() => this.deactivateUserOnClick(user.name, user.email, user.role)}>Deactivate User</button>
                                                </div>
                                            </>
                                        }
                                    />
                                </li>
                            );
                        })
                    }
                </ul>
            </div>
        );
    }
}


// ---------- Redux mapping: ----------
const mapStateToProps = state => {
    return {
        user: state.user
    };
};

export default connect(mapStateToProps)(Users);
